import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ClipboardIcon,
  CheckIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";

export default function TicketingListPage() {
  const { t } = useTranslation();
  const [events, setEvents] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Event?showAll=true`
      );
      const data = await response.json();
      setEvents(data);
    };

    fetchEvents();
  }, []);

  const handleCopy = (eventId) => {
    const url = `${window.location.origin}/event/${eventId}`;
    navigator.clipboard.writeText(url);
    setCopiedId(eventId);

    setTimeout(() => {
      setCopiedId(null);
    }, 1500);
  };

  if (!events.length) {
    return (
      <div className="text-center text-gray-500 py-20">
        {t("No events available")}
      </div>
    );
  }

  const handleDelete = async (event) => {
    const confirmed = window.confirm(
      `Voulez-vous supprimer l'événement "${event.name}" ?`
    );
    if (!confirmed) return;

    const response = await fetch(
      `${process.env.REACT_APP_API_BASE_URL}/api/Event/remove`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(event),
      }
    );

    if (response.ok) {
      setEvents((prev) => prev.filter((e) => e.id !== event.id));
    } else {
      alert("Une erreur est survenue lors de la suppression.");
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 text-primary shadow-2xl m-4">
      <h1 className="text-3xl font-bold text-center mb-6">
        {t("ticketing.title")}
      </h1>

      <div className="overflow-x-auto">
        <table className="table table-zebra w-full">
          <thead>
            <tr>
              <th>{t("ticketing.eventNameLabel")}</th>
              <th>{t("Date")}</th>
              <th>{t("ticketing.locationLabel")}</th>
              <th>{t("ticketing.ticketSold")}</th>
              <th>{t("URL")}</th>
              <th>Supprimer</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => {
              const isPast =
                new Date(event.eventDate) <
                new Date(new Date().setHours(0, 0, 0, 0));

              return (
                <tr
                  key={event.id}
                  className={isPast ? "text-gray-400 bg-gray-100" : ""}
                >
                  <td>{event.name}</td>
                  <td>{formatDate(event.eventDate)}</td>
                  <td>
                    {event.location.formattedAddress
                      ? event.location.formattedAddress
                      : `${event.location.latitude.toFixed(
                          4
                        )}, ${event.location.longitude.toFixed(4)}`}
                  </td>
                  <td>
                    {event.ticketsSold} / {event.maxTickets}
                  </td>
                  <td>
                    <button
                      className={`btn btn-sm transition-transform ${
                        isPast ? "hidden cursor-not-allowed" : "btn-ghost"
                      }`}
                      disabled={isPast}
                      onClick={() => handleCopy(event.id)}
                    >
                      <AnimatePresence mode="wait" initial={false}>
                        {copiedId === event.id ? (
                          <motion.span
                            key="check"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            transition={{ duration: 0.3 }}
                          >
                            <CheckIcon className="w-5 h-5 text-green-500" />
                          </motion.span>
                        ) : (
                          <motion.span
                            key="clipboard"
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 2 }}
                            transition={{ duration: 0.4 }}
                          >
                            <ClipboardIcon className="w-5 h-5 text-gray-700" />
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </button>
                  </td>
                  <td>
                    <button
                      onClick={() => handleDelete(event)}
                      className="btn btn-sm"
                    >
                      <TrashIcon className="w-5 h-5 text-red-600" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function formatDate(dateString) {
  const options = {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Date(dateString).toLocaleDateString(undefined, options);
}
